import { useCallback, useEffect, useRef, useState } from "react";
import Carousel from "react-bootstrap/Carousel";
import { Link } from "react-router-dom";
import "./HomePageCarousal.css";
import type { CSSProperties } from "react";
import type { HomeCarouselItem } from "../../types/homeCarousal";
import { API_URL, baseUrl } from "../../services/apiConfig";
import axiosInstance from "../../services/axios.instance";
import {
    car_home,
    dashboard_red_car,
    jeep_yellow_car,
    kirkos_merchant_white_car,
    lexus_black_car,
} from "../../assets/images";

type HomePageCarouselProps = {
    mode?: "default" | "landing-frame";
};

const normalizeUrl = (url: string): string => {
    if (!url) return "";
    // Fix double protocol bug (e.g. https://https://...) from API
    const fixed = url.replace(/^https?:\/\/https?:\/\//i, "https://");
    if (/^https?:\/\//i.test(fixed)) return fixed;
    return fixed;
};

const isExternalUrl = (url: string) => /^https?:\/\//i.test(url);

const resolveAction = (url?: string | null) => url || "";

const LANDING_FALLBACKS = [
    car_home,
    lexus_black_car,
    dashboard_red_car,
    jeep_yellow_car,
    kirkos_merchant_white_car,
];

const DEFAULT_INTERVAL_MS = 4000;

const getLandingImage = (item: HomeCarouselItem | undefined, fallbackIndex: number) =>
    normalizeUrl(item?.desktop_image_url || item?.image_url || "") ||
    LANDING_FALLBACKS[fallbackIndex % LANDING_FALLBACKS.length];

const HomePageCarousel = ({ mode = "default" }: HomePageCarouselProps) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [items, setItems] = useState<HomeCarouselItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    // Used by landing-frame auto-loop
    const isPaused = useRef(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        const controller = new AbortController();

        const loadCarousel = async () => {
            const startTime = Date.now();
            try {
                setIsLoading(true);
                const response = await axiosInstance.get(
                    API_URL.CAROUSAL_HOME,
                    {
                        signal: controller.signal,
                        headers: {
                            Accept: "application/json",
                        },
                    },
                );

                const data = response.data as HomeCarouselItem[];
                const validItems = Array.isArray(data) ? data : [];
                setItems(validItems);

                // Preload the first image so the skeleton doesn't disappear until the image is ready
                if (validItems.length > 0) {
                    const firstImage = mode === "landing-frame"
                        ? getLandingImage(validItems[0], 0)
                        : normalizeUrl(
                              window.innerWidth <= 767 && validItems[0].mobile_image_url
                                  ? validItems[0].mobile_image_url
                                  : validItems[0].desktop_image_url || validItems[0].image_url
                          );
                    if (firstImage) {
                        await new Promise((resolve) => {
                            const img = new Image();
                            img.src = firstImage;
                            img.onload = resolve;
                            img.onerror = resolve;
                        });
                    }
                }
            } catch (error: any) {
                if (error?.name !== "CanceledError" && error?.code !== "ERR_CANCELED") {
                    console.error("Failed to load homepage carousel", error);
                    setItems([]);
                }
            } finally {
                const elapsed = Date.now() - startTime;
                if (elapsed < 600) {
                    await new Promise((res) => setTimeout(res, 600 - elapsed));
                }
                setIsLoading(false);
            }
        };

        void loadCarousel();

        return () => controller.abort();
    }, []);

    // ── Auto-loop for landing-frame mode ──────────────────────────────────
    useEffect(() => {
        if (mode !== "landing-frame" || items.length <= 1) return;

        const tick = () => {
            if (!isPaused.current) {
                setActiveIndex((prev) => (prev + 1) % items.length);
            }
        };

        // Use the current slide's interval, falling back to default
        const ms = items[activeIndex]?.interval_ms ?? DEFAULT_INTERVAL_MS;
        intervalRef.current = setInterval(tick, ms);

        return () => {
            if (intervalRef.current !== null) clearInterval(intervalRef.current);
        };
    }, [mode, items, activeIndex]);

    const handleMouseEnter = useCallback(() => {
        isPaused.current = true;
    }, []);

    const handleMouseLeave = useCallback(() => {
        isPaused.current = false;
    }, []);

    if (isLoading) {
        if (mode === "landing-frame") {
            return (
                <section
                    className="homepage-carousel homepage-carousel--landing"
                    aria-label="Loading featured carousel"
                >
                    <div className="homepage-carousel__landing-shell">
                        <div className="homepage-carousel__landing-stage homepage-carousel__loading" />
                    </div>
                </section>
            );
        }

        return (
            <section className="homepage-carousel" aria-label="Loading featured carousel">
                <div className="homepage-carousel__shell">
                    <div className="homepage-carousel__slide" style={{ background: "#252421" }}>
                        <div className="homepage-carousel__layout">
                            <div className="homepage-carousel__copy" style={{ gap: "1rem" }}>
                                <div
                                    className="homepage-carousel__loading"
                                    style={{ width: "120px", height: "28px", minHeight: "auto", borderRadius: "999px" }}
                                />
                                <div
                                    className="homepage-carousel__loading"
                                    style={{ width: "80%", height: "48px", minHeight: "auto", borderRadius: "8px", marginTop: "0.5rem" }}
                                />
                                <div
                                    className="homepage-carousel__loading"
                                    style={{ width: "50%", height: "24px", minHeight: "auto", borderRadius: "8px" }}
                                />
                                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "1rem" }}>
                                    <div
                                        className="homepage-carousel__loading"
                                        style={{ width: "95%", height: "18px", minHeight: "auto", borderRadius: "4px" }}
                                    />
                                    <div
                                        className="homepage-carousel__loading"
                                        style={{ width: "90%", height: "18px", minHeight: "auto", borderRadius: "4px" }}
                                    />
                                    <div
                                        className="homepage-carousel__loading"
                                        style={{ width: "75%", height: "18px", minHeight: "auto", borderRadius: "4px" }}
                                    />
                                </div>
                            </div>
                            <div className="homepage-carousel__media">
                                <div
                                    className="homepage-carousel__loading"
                                    style={{ width: "100%", height: "240px", minHeight: "auto", borderRadius: "16px" }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (!items.length) {
        return null;
    }

    const activeItem = items[activeIndex];
    if (mode === "landing-frame") {
        return (
            <section
                className="homepage-carousel homepage-carousel--landing"
                aria-label="Featured offers carousel"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className="homepage-carousel__landing-shell">
                    <div
                        className="homepage-carousel__landing-stage"
                        style={
                            {
                                "--landing-accent": activeItem.accent_color,
                                "--landing-bg": activeItem.background_color,
                            } as CSSProperties
                        }
                    >
                        <img
                            src={getLandingImage(activeItem, activeIndex)}
                            alt={activeItem.image_alt || `${activeItem.title || "Luxury car rental"} - Kirkos Dubai`}
                            className="homepage-carousel__landing-image"
                            onError={(event) => {
                                event.currentTarget.src =
                                    LANDING_FALLBACKS[activeIndex % LANDING_FALLBACKS.length];
                            }}
                        />

                        {items.length > 1 && (
                            <>
                                <button
                                    className="carousel-control-prev"
                                    type="button"
                                    onClick={() => setActiveIndex((prev) => (prev - 1 + items.length) % items.length)}
                                >
                                    <span className="carousel-control-prev-icon" aria-hidden="true" />
                                </button>
                                <button
                                    className="carousel-control-next"
                                    type="button"
                                    onClick={() => setActiveIndex((prev) => (prev + 1) % items.length)}
                                >
                                    <span className="carousel-control-next-icon" aria-hidden="true" />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="homepage-carousel" aria-label="Featured offers carousel">
            <div className="homepage-carousel__shell">
                <Carousel
                    activeIndex={activeIndex}
                    onSelect={(selectedIndex) => setActiveIndex(selectedIndex)}
                    controls={items.length > 1}
                    indicators={false}
                    fade
                    pause="hover"
                    touch
                    keyboard
                    className="homepage-carousel__carousel"
                >
                    {items.map((item, index) => {
                        const primaryHref = resolveAction(item.primary_action_url);
                        const secondaryHref = resolveAction(item.secondary_action_url);
                        const primaryExternal = isExternalUrl(primaryHref);
                        const secondaryExternal = isExternalUrl(secondaryHref);

                        return (
                            <Carousel.Item key={item.id} interval={item.interval_ms}>
                                <div
                                    className="homepage-carousel__slide"
                                    style={
                                        {
                                            "--slide-bg": item.background_color,
                                            "--slide-accent": item.accent_color,
                                            "--slide-text": item.text_color,
                                            background: `linear-gradient(135deg, ${item.background_color} 0%, #0f0f0d 100%)`,
                                        } as CSSProperties
                                    }
                                >
                                    <div className="homepage-carousel__layout">
                                        <div className="homepage-carousel__copy">
                                            <div className="homepage-carousel__badge">
                                                <span className="homepage-carousel__dot" />
                                                {item.badge_text || "Featured offer"}
                                            </div>
                                            <h2 className="homepage-carousel__title">{item.title}</h2>
                                            <p className="homepage-carousel__subtitle">{item.subtitle}</p>
                                            <p className="homepage-carousel__description">{item.description}</p>
                                            {/* <div className="homepage-carousel__actions">
                                                {primaryExternal ? (
                                                    <a
                                                        href={primaryHref}
                                                        className="homepage-carousel__action homepage-carousel__action--solid"
                                                        target={item.open_in_new_tab ? "_blank" : "_self"}
                                                        rel={item.open_in_new_tab ? "noreferrer" : undefined}
                                                    >
                                                        {item.primary_action_label}
                                                    </a>
                                                ) : (
                                                    <Link
                                                        to={primaryHref}
                                                        className="homepage-carousel__action homepage-carousel__action--solid"
                                                    >
                                                        {item.primary_action_label}
                                                    </Link>
                                                )}

                                                {item.secondary_action_label && secondaryHref ? (
                                                    secondaryExternal ? (
                                                        <a
                                                            href={secondaryHref}
                                                            className="homepage-carousel__action homepage-carousel__action--outline"
                                                            target={item.open_in_new_tab ? "_blank" : "_self"}
                                                            rel={item.open_in_new_tab ? "noreferrer" : undefined}
                                                        >
                                                            {item.secondary_action_label}
                                                        </a>
                                                    ) : (
                                                        <Link
                                                            to={secondaryHref}
                                                            className="homepage-carousel__action homepage-carousel__action--outline"
                                                        >
                                                            {item.secondary_action_label}
                                                        </Link>
                                                    )
                                                ) : null}
                                            </div> */}
                                        </div>

                                        <div className="homepage-carousel__media">
                                            <img
                                                src={normalizeUrl(
                                                    window.innerWidth <= 767 && item.mobile_image_url
                                                        ? item.mobile_image_url
                                                        : item.desktop_image_url || item.image_url
                                                )}
                                                alt={item.image_alt}
                                                loading={index === 0 ? "eager" : "lazy"}
                                                decoding="async"
                                                className="homepage-carousel__img"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Carousel.Item>
                        );
                    })}
                </Carousel>
            </div>
        </section>
    );
};

export default HomePageCarousel;
